import { useCallback, useState } from "react";
import type { MenuItem } from "@/types/domain";

const STORAGE_KEY_PREFIX = "vecta-nav-collapsed-sections";

export const collapsedSectionsKey = (username: string) => `${STORAGE_KEY_PREFIX}:${username}`;

export const loadCollapsedSections = (key: string): string[] => {
  try {
    const parsed: unknown = JSON.parse(localStorage.getItem(key) ?? "[]");
    return Array.isArray(parsed) ? parsed.filter((v): v is string => typeof v === "string") : [];
  } catch {
    return [];
  }
}

const saveCollapsedSections = (key: string, sections: string[]) => {
  try {
    localStorage.setItem(key, JSON.stringify(sections));
  } catch {
    // storage unavailable (private mode or quota); collapse state just won't persist
  }
}

export const isItemSelected = (item: MenuItem): boolean =>
  item.isSelected || !!item.children?.some((c) => c.isSelected);

export const getSectionName = (items: MenuItem[], index: number): string | null => {
  for (let i = index; i >= 0; i--) {
    if (items[i].section) return items[i].section!;
    if (items[i].startsNewGroup) return null;
  }
  return null;
}

export const getSelectedSections = (items: MenuItem[]): Set<string> => {
  const selected = new Set<string>();
  items.forEach((item, index) => {
    const section = getSectionName(items, index);
    if (section && isItemSelected(item)) selected.add(section);
  });
  return selected;
}

export const useCollapsedSections = (key: string) => {
  const [state, setState] = useState(() => ({ key, sections: loadCollapsedSections(key) }));
  const sections = state.key === key ? state.sections : loadCollapsedSections(key);

  const toggle = useCallback((section: string) => {
    const current = loadCollapsedSections(key);
    const next = current.includes(section) ? current.filter((s) => s !== section) : [...current, section];
    saveCollapsedSections(key, next);
    setState({ key, sections: next });
  }, [key]);

  return { collapsed: new Set(sections), toggle };
}
